import React, { useEffect } from 'react';
import ToasterContextProvider from '@app/component/overlays/toaster/context/ToasterContextProvider';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import SignInPage from '@app/site/authentication/page/SignInPage';
import SignUpPage from '@app/site/registration/page/SignUpPage';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import ProfileVerificationFlowPage from '@app/site/registration/flow/ProfileVerificationFlowPage';
import RegistrationFlowPage from '@app/site/registration/page/RegistrationFlowPage';
import VerifyProfilePage from '@app/site/registration/page/VerifyProfilePage';
import ResetPasswordRequestPage from '@app/site/profile-reset-password/page/ResetPasswordRequestPage';
import ResetPasswordPage from '@app/site/profile-reset-password/page/ResetPasswordPage';
import SecuredLayout from '@app/site/secured/layout/SecuredLayout';
import useSiteState from '@app/state/SiteState';
import SecurityHelper from '@util/SecurityHelper';
import ErrorPageNotFound from '@app/site/system/page/ErrorPageNotFound';
import PropertyFormFragment from '@app/site/secured/settings/fragment/properties/PropertyFormFragment';
import DashboardPage from '@app/site/secured/dashboard/page/DashboardPage';
import CompanyHotelRoomMappingPage from '@app/site/secured/room-mapping/page/CompanyHotelRoomMappingPage';
import AlertPage from '@app/site/secured/alert/page/AlertPage';
import AlertExecutionPage from '@app/site/secured/alert-execution/page/AlertExecutionPage';
import ProtectedRoute from '@app/site/ProtectedRoute';
import ErrorPageAccessDenied from '@app/site/system/page/ErrorPageAccessDenied';
import Authority from '@app/model/authentication/Authority';
import AcceptCompanyProfileInvitationPage from '@app/site/registration/page/AcceptCompanyProfileInvitationPage';
import ACLAuthority from '@app/model/profile/ACLAuthority';
import WebSocketWrapper from '@app/site/WebSocketWrapper';
import { DashboardView } from '@app/state/DashboardState';
import { GoogleOAuthProvider } from '@react-oauth/google';
import process from 'process';
import ExecuteEditAccountFlowPage from '@app/site/secured/settings/fragment/properties/ExecuteEditAccountFlowPage';
import AfterAuthenticationHandler from '@app/site/authentication/page/AfterAuthenticationHandler';
import WebHotelierPage from '@app/site/integration/webhotelier/page/WebHotelierPage';
import { MixpanelProvider } from '@app/site/mixpanel/MixpanelProvider';
import { WebHotelierLandingPage } from '@app/site/integration/webhotelier/page/WebHotelierLandingPage';
import { WebHotelierErrorPage } from '@app/site/integration/webhotelier/page/WebHotelierErrorPage';

const AppRouter = React.memo(() => {
	const queryClient = new QueryClient();
	const siteState = useSiteState();

	const MIX_PANEL_TOKEN = 'b1b1acd50d7c4aaec4d485501b02267b';
	const MIX_PANEL_CONFIG = {
		autocapture: true,
		record_sessions_percent: 100,
		record_mask_text_selector: ""
	};

	return <WebSocketWrapper>
		<QueryClientProvider client={queryClient}>
			<ToasterContextProvider>
				<GoogleOAuthProvider clientId={process.env['REACT_APP_GOOGLE_CLIENT_ID']!}>
					<MixpanelProvider config={MIX_PANEL_CONFIG} token={MIX_PANEL_TOKEN} name="Mixpanel">
						<BrowserRouter>
							<Routes>
								<Route path="*" element={<ErrorPageNotFound />} />
								<Route path="secured/*" element={<ErrorPageNotFound />} />
								<Route path="access-denied" element={<ErrorPageAccessDenied />} />
								<Route path="/" element={SecurityHelper.isAuthenticated(siteState.principal) ?
									<Navigate to={'/secured/dashboard'} replace /> :
									<Navigate to={'/sign-in'} replace />}>
									<Route index element={<span>Index</span>} />
								</Route>
								<Route path="/sign-in" element={<SignInPage />} />
								<Route path="/sign-up" element={<SignUpPage />} />
								<Route path="/webhotelier-welcome" element={<WebHotelierLandingPage webhotelierId={undefined} />} />
								<Route path="/webhotelier-help" element={<WebHotelierErrorPage />} />
								<Route path="/oauth2/redirect" element={<AfterAuthenticationHandler />}></Route>
								<Route path="/verification-pending" element={<ProfileVerificationFlowPage />} />
								<Route path="/registration" element={<RegistrationFlowPage />} />
								<Route path="/verify-profile/:key" element={<VerifyProfilePage />} />
								<Route path="/accept-invitation/:key" element={<AcceptCompanyProfileInvitationPage />} />
								<Route path="/forgot-password" element={<ResetPasswordRequestPage />} />
								<Route path="/reset-password/:key" element={<ResetPasswordPage />} />
								<Route path="/webhotelier/callback" element={<WebHotelierPage />} />
								<Route path="/secured" element={<ProtectedRoute />}>
									<Route path="/secured/parity" element={<SecuredLayout>
										<DashboardPage dashboardView={DashboardView.PARITY} />
									</SecuredLayout>} />
									<Route path="/secured/dashboard" element={<SecuredLayout>
										<DashboardPage dashboardView={DashboardView.CALENDAR} />
									</SecuredLayout>} />
									<Route path="/secured/dashboard/table" element={<SecuredLayout>
										<DashboardPage dashboardView={DashboardView.TABLE} />
									</SecuredLayout>} />
									<Route path="/secured/dashboard/graph" element={<SecuredLayout>
										<DashboardPage dashboardView={DashboardView.GRAPH} />
									</SecuredLayout>} />
									<Route path="/secured/dashboard/settings" element={<SecuredLayout hideCompanyHotelSelector>
										<DashboardPage dashboardView={DashboardView.DASHBOARD_SETTINGS} />
									</SecuredLayout>} />
									<Route path="/secured/market-state-export" element={<SecuredLayout>
										<DashboardPage dashboardView={DashboardView.MARKET_STATE_EXPORT} />
									</SecuredLayout>} />
									<Route path="/secured/saving-properties/:hotelId"
												 element={<SecuredLayout lockCompanyHotelSelector><ExecuteEditAccountFlowPage /></SecuredLayout>} />
									<Route path="/secured/alerts" element={<SecuredLayout lockCompanyHotelSelector><DashboardPage dashboardView={DashboardView.ALERTS} /></SecuredLayout>} />
									<Route path="/secured/account/settings" element={<SecuredLayout hideCompanyHotelSelector><DashboardPage
										dashboardView={DashboardView.SETTINGS_GENERAL} /></SecuredLayout>} />
									<Route path="/secured/account/settings/properties" element={<SecuredLayout hideCompanyHotelSelector><DashboardPage
										dashboardView={DashboardView.SETTINGS_PROPERTIES} /></SecuredLayout>} />
									<Route path="/secured/account/settings/properties-access" element={<SecuredLayout lockCompanyHotelSelector><DashboardPage
										dashboardView={DashboardView.SETTINGS_PROPERTY_ACCESS} /></SecuredLayout>} />
									<Route path="/secured/alert/:alertType/:alertId"
												 element={<SecuredLayout lockCompanyHotelSelector><AlertPage /></SecuredLayout>} />
									<Route path="/secured/duplicate-alert/:alertType/:alertId"
												 element={<SecuredLayout lockCompanyHotelSelector><AlertPage isCopy /></SecuredLayout>} />
									<Route path="/secured/create-alert/:alertType"
												 element={<SecuredLayout lockCompanyHotelSelector><AlertPage /></SecuredLayout>} />
									<Route path="/secured/alert-execution/:alertExecutionId"
												 element={<SecuredLayout lockCompanyHotelSelector><AlertExecutionPage /></SecuredLayout>} />
									<Route path="/secured/settings/properties"
												 element={
													 <SecuredLayout lockCompanyHotelSelector><DashboardPage
														 dashboardView={DashboardView.SETTINGS_PROPERTIES} /></SecuredLayout>
												 } />
									<Route path="/secured/settings/property/:hotelId?"
												 element={<SecuredLayout lockCompanyHotelSelector><PropertyFormFragment /></SecuredLayout>} />
									<Route path="/secured/room-mapping"
												 element={<SecuredLayout lockCompanyHotelSelector
																								 hasAclAuthority={ACLAuthority.WRITE}><CompanyHotelRoomMappingPage /></SecuredLayout>} />
								</Route>

							</Routes>
						</BrowserRouter>
					</MixpanelProvider>
				</GoogleOAuthProvider>
			</ToasterContextProvider>
			{<ReactQueryDevtools initialIsOpen={false} />}
		</QueryClientProvider>
	</WebSocketWrapper>;
});

export default AppRouter;